import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, HardHat, Loader2, Wallet } from 'lucide-react';
import { createJob, formatUsd, buildAutoInvoices } from '@/lib/jobsData';
import { toast } from 'sonner';

const initial = {
  name: '',
  client_id: '',
  address: '',
  contract: '',
  job_type: 'fixed',
  started: '',
  est_end: '',
};

export const CreateJobDialog = ({ open, onOpenChange, clients = [], onCreated }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setForm(initial);
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const setField = (k) => (e) => {
    const v = e?.target?.value ?? '';
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Show only clients (not leads) in picker
  const clientOptions = useMemo(
    () => (clients || []).filter((c) => c?.type === 'client'),
    [clients]
  );

  const previewInvoices = useMemo(() => {
    const c = Number(form.contract) || 0;
    if (c <= 0) return [];
    return buildAutoInvoices({
      contract: c,
      started: form.started,
      estEnd: form.est_end,
    });
  }, [form.contract, form.started, form.est_end]);

  const validate = () => {
    if (!form.name.trim()) return 'Job name is required.';
    if (!form.client_id) return 'Select a client.';
    if (form.contract === '' || Number.isNaN(Number(form.contract)))
      return 'Contract amount is required.';
    if (Number(form.contract) <= 0) return 'Contract must be greater than zero.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const selected = clientOptions.find((c) => c.id === form.client_id);
      const created = await createJob({
        name: form.name.trim(),
        client: selected?.name || '',
        client_id: form.client_id,
        address: form.address.trim(),
        contract: Number(form.contract),
        job_type: form.job_type,
        started: form.started.trim() || 'TBD',
        est_end: form.est_end.trim() || 'TBD',
      });
      toast.success(`Job created \u2014 ${created.name}`);
      onOpenChange?.(false);
      onCreated?.(created);
    } catch (err) {
      setError(err?.message || 'Failed to create job.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-lg"
        data-testid="create-job-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <HardHat size={16} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              New Job
            </span>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            Create new job
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            We'll auto-generate a 40 / 30 / 30 invoice schedule once you set the
            contract amount.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-1" noValidate>
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Job name *
            </Label>
            <Input
              value={form.name}
              onChange={setField('name')}
              placeholder="e.g. Two-Bath Renovation"
              autoComplete="off"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
              data-testid="create-job-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Client *
              </Label>
              <Select
                value={form.client_id}
                onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
              >
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border"
                  data-testid="create-job-client"
                >
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                  {clientOptions.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No clients yet. Convert a lead first.
                    </div>
                  ) : (
                    clientOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Job type
              </Label>
              <Select
                value={form.job_type}
                onValueChange={(v) => setForm((f) => ({ ...f, job_type: v }))}
              >
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border"
                  data-testid="create-job-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="tm">Time &amp; materials</SelectItem>
                  <SelectItem value="cost-plus">Cost-plus</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Address
            </Label>
            <Input
              value={form.address}
              onChange={setField('address')}
              placeholder="Street, City State ZIP"
              autoComplete="off"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
              data-testid="create-job-address"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Contract (USD) *
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="100"
                value={form.contract}
                onChange={setField('contract')}
                placeholder="25000"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="create-job-contract"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Start date
              </Label>
              <Input
                value={form.started}
                onChange={setField('started')}
                placeholder="e.g. May 7, 2026"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="create-job-start"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Est. end
              </Label>
              <Input
                value={form.est_end}
                onChange={setField('est_end')}
                placeholder="TBD"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="create-job-end"
              />
            </div>
          </div>

          {previewInvoices.length > 0 ? (
            <div
              className="rounded-md border border-border bg-[hsl(var(--background))]/40 p-3"
              data-testid="create-job-invoice-preview"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                <Wallet size={12} className="text-[hsl(var(--primary))]" />
                Auto-generated invoices
              </div>
              <div className="mt-2 space-y-1.5">
                {previewInvoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-foreground/90 truncate mr-3">{inv.desc}</span>
                    <span className="text-foreground font-semibold">
                      {formatUsd(inv.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {error ? (
            <Alert
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="create-job-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="create-job-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating
                </>
              ) : (
                <>Create job</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateJobDialog;
